import React, { useState, useEffect } from 'react';
import BulletinTable from '../Components/BulletinTable';
import BulletinModal from '../Components/BulletinModal';
import bulletinService from '../services/bulletinService';

export default function BulletinBoard() {
  const [bulletins, setBulletins] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentBulletin, setCurrentBulletin] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    hasNext: false,
    hasPrev: false
  });
  const [loading, setLoading] = useState(true);
  const itemsPerPage = 10;

  // Load bulletins via service with pagination
  const loadBulletins = async (page = 1) => {
    try {
      setLoading(true);
      const result = await bulletinService.getAllBulletins(page, itemsPerPage);
      console.log('Loaded bulletins:', result);
      
      // Use the result directly from the service
      setBulletins(result.bulletins);
      setPagination(result.pagination);
    } catch (e) {
      console.error('Failed to load bulletins', e);
      setBulletins([]);
      setPagination({
        currentPage: 1,
        totalPages: 1,
        totalRecords: 0,
        hasNext: false,
        hasPrev: false
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBulletins(1);
  }, []);

  const handleAddBulletin = () => {
    setCurrentBulletin(null);
    setIsModalOpen(true);
  };

  const handleEditBulletin = (bulletin) => {
    setCurrentBulletin(bulletin);
    setIsModalOpen(true);
  };

  const handleDeleteBulletin = async (id) => {
    try {
      console.log('Attempting to delete bulletin with ID:', id);
      // Show confirmation dialog
      const confirmDelete = window.confirm('Are you sure you want to delete this bulletin?');
      if (!confirmDelete) return;

      await bulletinService.deleteBulletin(id);
      
      // Show success message
      alert('Bulletin deleted successfully!');
      
      // Refresh the current page data
      await loadBulletins(pagination.currentPage);
    } catch (error) {
      console.error('Error deleting bulletin:', error);
      alert('Failed to delete bulletin. Please try again.');
    }
  };

  const handleSaveBulletin = async (bulletinData) => {
    try {
      // Normalize priority to match table styles
      const normalized = {
        ...bulletinData,
        priority: (bulletinData.priority || 'medium').toLowerCase(),
      };
      
      if (normalized.id) {
        // Update existing bulletin
        console.log('Updating bulletin with ID:', normalized.id);
        const updatedBulletin = await bulletinService.updateBulletin(normalized.id, normalized);
        console.log('Update response:', updatedBulletin);
        
        // Update the bulletin in the state
        setBulletins(bulletins.map(b => (b.id === normalized.id ? updatedBulletin : b)));
        alert('Bulletin updated successfully!');
      } else {
        // Create new bulletin
        console.log('Creating new bulletin');
        const newBulletin = await bulletinService.createBulletin(normalized);
        console.log('Create response:', newBulletin);
        setBulletins([...bulletins, newBulletin]);
        alert('Bulletin created successfully!');
      }
      
      // Refresh the current page data to ensure consistency
      await loadBulletins(pagination.currentPage);
    } catch (error) {
      console.error('Error saving bulletin:', error);
      alert(`Failed to save bulletin: ${error.message || 'Please try again.'}`);
      throw error; // Re-throw to prevent modal from closing
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
      <div className="sm:flex sm:justify-between sm:items-center mb-8">
        <h1 className="text-2xl md:text-3xl ml-3 text-white font-bold">Bulletin Board</h1>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
        </div>
      ) : (
        <>
          <BulletinTable 
            bulletinsData={bulletins}
            onAddBulletin={handleAddBulletin}
            onEditBulletin={handleEditBulletin}
            onDeleteBulletin={handleDeleteBulletin}
          />

          {/* Pagination Controls */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-gray-300">
                Showing {((pagination.currentPage - 1) * itemsPerPage) + 1} to {Math.min(pagination.currentPage * itemsPerPage, pagination.totalRecords)} of {pagination.totalRecords} results
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => loadBulletins(pagination.currentPage - 1)}
                  disabled={!pagination.hasPrev}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    pagination.hasPrev 
                      ? 'bg-blue-600 text-white hover:bg-blue-700' 
                      : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Previous
                </button>
                
                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => loadBulletins(page)}
                    className={`px-3 py-2 rounded-md text-sm font-medium ${
                      page === pagination.currentPage
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                
                <button
                  onClick={() => loadBulletins(pagination.currentPage + 1)}
                  disabled={!pagination.hasNext}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    pagination.hasNext 
                      ? 'bg-blue-600 text-white hover:bg-blue-700' 
                      : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <BulletinModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        bulletin={currentBulletin}
        onSave={handleSaveBulletin}
      />
    </div>
  );
}